import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Bold, Copy, Eye, Image as ImageIcon, Italic, Link2, List, ListOrdered, MoreVertical, Pencil, Plus, Quote, Search, Trash2, Type } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ImageField, absoluteMediaUrl } from "@/components/ImageField";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { ArticleContent } from "@/components/ArticleContent";
import { toHtml, sanitizePastedHtml, plainTextToParagraphs } from "@/lib/article";

const CATEGORIES = ["Tendenze arredo", "Ristrutturare", "Stili di arredamento", "Abbinamento colori e materiali", "Cucina", "Pavimento e Rivestimenti", "Complementi di Design", "Consigli", "Ispirazioni", "Diario"];

const STATUS_BADGE = {
  bozza: "text-taupe border-hair",
  pubblicato: "text-emerald-700 border-emerald-700/40",
  programmato: "text-amber-700 border-amber-700/40",
};

const btn = "min-h-[40px] inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border transition-colors disabled:opacity-50";
const sel = "h-11 border border-hair bg-white px-3 text-sm rounded-none";

const slugify = (t) => (t || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const emptyPost = { title: "", slug: "", author: "Giovanna Matino", category: "Diario", tags: [], excerpt: "", content: "", cover_image: "", gallery: [], published: false, published_at: "", seo_title: "", seo_description: "", canonical_url: "" };

/* Editor WYSIWYG articolo: toolbar STICKY sempre visibile + area di scrittura con scroll indipendente.
   I bottoni usano onMouseDown preventDefault per NON rubare la selezione del testo
   (era la causa di Grassetto/H2/H3 "che non funzionano").
   La tipografia dell'area di scrittura è .article-content (index.css): la STESSA usata da
   anteprima e pagina pubblica — ciò che vedi mentre scrivi è ciò che esce sul sito. */
const FONT_SIZES = [["2", "Piccolo"], ["3", "Normale"], ["5", "Grande"], ["6", "Molto grande"]];

export function ArticleEditor({ value, onChange }) {  const ref = useRef(null);
  const loaded = useRef(false);
  useEffect(() => {
    if (ref.current && !loaded.current) { ref.current.innerHTML = toHtml(value); loaded.current = true; }
  }, [value]);
  const emit = () => onChange(ref.current.innerHTML);
  const keep = (e) => e.preventDefault(); // mantiene la selezione nel testo
  const cmd = (c, arg = null) => { document.execCommand(c, false, arg); emit(); };
  const block = (tag) => cmd("formatBlock", `<${tag}>`); // veri heading HTML, compatibile tutti i browser
  /* Copia-incolla normalizzato: mantiene grassetto, corsivo, titoli, elenchi, link e citazioni;
     rimuove font/span/stili/dimensioni estranee. Le dimensioni font intenzionali si impostano
     dalla toolbar e vengono conservate dal salvataggio. */
  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData?.getData("text/html");
    const text = e.clipboardData?.getData("text/plain") || "";
    const clean = html ? sanitizePastedHtml(html) : plainTextToParagraphs(text);
    if (clean) document.execCommand("insertHTML", false, clean);
    emit();
  };
  const insertLink = () => {
    const url = window.prompt("Indirizzo del link (https://…)");
    if (url) cmd("createLink", url);
  };
  const insertImage = () => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async () => {
      if (!inp.files?.[0]) return;
      try {
        const fd = new FormData();
        fd.append("file", inp.files[0]);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const alt = window.prompt("Testo alternativo (ALT) — importante per SEO e accessibilità:", "") || "";
        const cap = window.prompt("Didascalia (facoltativa, lascia vuoto per ometterla):", "") || "";
        // Nessuno stile inline: margini/dimensioni li dà .article-content, identico ovunque
        const html = cap
          ? `<figure><img src="${data.url}" alt="${alt.replace(/"/g, "&quot;")}"/><figcaption>${cap}</figcaption></figure><p><br/></p>`
          : `<img src="${data.url}" alt="${alt.replace(/"/g, "&quot;")}"/><p><br/></p>`;
        document.execCommand("insertHTML", false, html);
        emit();
        toast.success("Immagine inserita nel testo");
      } catch { toast.error("Upload immagine non riuscito"); }
    };
    inp.click();
  };
  const tbtn = "min-w-[36px] min-h-[36px] inline-flex items-center justify-center border border-hair hover:border-ink hover:bg-bone text-xs transition-colors shrink-0";
  return (
    <div className="border border-hair bg-white">
      {/* Toolbar sticky: resta sempre visibile mentre il testo scorre sotto */}
      <div className="sticky top-0 z-10 bg-white border-b border-hair p-2 flex gap-1.5 flex-wrap items-center" data-testid="blog-editor-toolbar">
        <button type="button" onMouseDown={keep} onClick={() => block("p")} className={tbtn} title="Paragrafo" data-testid="blog-ed-p"><Type size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => block("h2")} className={`${tbtn} font-serif font-bold`} title="Titolo H2" data-testid="blog-ed-h2">H2</button>
        <button type="button" onMouseDown={keep} onClick={() => block("h3")} className={`${tbtn} font-serif`} title="Titolo H3" data-testid="blog-ed-h3">H3</button>
        <select
          defaultValue=""
          onMouseDown={keep}
          onChange={(e) => { if (e.target.value) { cmd("fontSize", e.target.value); e.target.value = ""; } }}
          className="h-9 border border-hair bg-white px-2 text-xs rounded-none shrink-0"
          title="Dimensione font" data-testid="blog-ed-fontsize">
          <option value="" disabled>Font</option>
          {FONT_SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="button" onMouseDown={keep} onClick={() => cmd("bold")} className={tbtn} title="Grassetto" data-testid="blog-ed-bold"><Bold size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("italic")} className={tbtn} title="Corsivo" data-testid="blog-ed-italic"><Italic size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("insertUnorderedList")} className={tbtn} title="Elenco puntato" data-testid="blog-ed-ul"><List size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("insertOrderedList")} className={tbtn} title="Elenco numerato" data-testid="blog-ed-ol"><ListOrdered size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => block("blockquote")} className={tbtn} title="Citazione" data-testid="blog-ed-quote"><Quote size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={insertLink} className={tbtn} title="Inserisci link" data-testid="blog-ed-link"><Link2 size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={insertImage} className={`${tbtn} text-gold border-gold/50`} title="Inserisci immagine nel punto del cursore" data-testid="blog-ed-image"><ImageIcon size={13} /></button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("justifyLeft")} className={tbtn} title="Allinea a sinistra" data-testid="blog-ed-left">⇤</button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("justifyCenter")} className={tbtn} title="Centra" data-testid="blog-ed-center">↔</button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("justifyRight")} className={tbtn} title="Allinea a destra" data-testid="blog-ed-right">⇥</button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("undo")} className={tbtn} title="Annulla" data-testid="blog-ed-undo">↺</button>
        <button type="button" onMouseDown={keep} onClick={() => cmd("redo")} className={tbtn} title="Ripristina" data-testid="blog-ed-redo">↻</button>
      </div>
      {/* Finestra articolo con scroll verticale indipendente — stessa tipografia di anteprima e sito */}
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onPaste={onPaste}
        className="article-content article-content--editing h-[52vh] min-h-[280px] overflow-y-auto p-5 bg-white focus:outline-none"
        data-testid="blog-content-editor"
      />
    </div>
  );
}

export function BlogPanel() {
  const [posts, setPosts] = useState(null);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fTag, setFTag] = useState("");
  const [editing, setEditing] = useState(null); // articolo in editor (null = lista)
  const [form, setForm] = useState(emptyPost);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (fStatus) params.set("status", fStatus);
      if (fCategory) params.set("category", fCategory);
      if (fTag) params.set("tag", fTag);
      const { data } = await api.get(`/admin/blog?${params}`);
      setPosts(data);
    } catch { toast.error("Errore caricamento articoli"); }
  }, [search, fStatus, fCategory, fTag]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const allTags = useMemo(() => [...new Set((posts || []).flatMap((p) => p.tags || []))], [posts]);

  const openEditor = (p) => {
    setForm(p ? { ...emptyPost, ...p, tags: p.tags || [], gallery: p.gallery || [] } : { ...emptyPost, published_at: "" });
    setEditing(p || { id: null });
  };

  const save = async (patch = {}) => {
    const payload = { ...form, ...patch };
    if (!payload.title.trim()) { toast.error("Il titolo è obbligatorio"); return null; }
    setBusy(true);
    try {
      const body = { ...payload, slug: payload.slug || slugify(payload.title) };
      const { data } = form.id
        ? await api.put(`/admin/blog/${form.id}`, body)
        : await api.post("/admin/blog", body);
      setForm((f) => ({ ...f, ...data }));
      setEditing(data);
      load();
      return data;
    } catch (err) { toast.error(err.response?.data?.detail || "Salvataggio non riuscito"); return null; }
    finally { setBusy(false); }
  };

  const saveDraft = async () => { const d = await save({ published: false }); if (d) toast.success("Bozza salvata"); };
  const publish = async () => {
    const at = form.published_at;
    const scheduled = at && new Date(at) > new Date();
    const d = await save({ published: true, published_at: at || new Date().toISOString() });
    if (d) toast.success(scheduled ? `Articolo programmato per ${fmtDate(d.published_at)}` : "Articolo pubblicato");
  };
  const unpublish = async () => { const d = await save({ published: false }); if (d) toast.success("Articolo ritirato dalla pubblicazione"); };

  const quickToggle = async (p) => {
    try {
      await api.put(`/admin/blog/${p.id}`, { published: !p.published, published_at: p.published ? "" : new Date().toISOString() });
      toast.success(p.published ? "Articolo ritirato" : "Articolo pubblicato");
      load();
    } catch { toast.error("Operazione non riuscita"); }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/admin/blog/${p.id}/duplicate`); toast.success("Articolo duplicato come bozza"); load(); }
    catch { toast.error("Duplicazione non riuscita"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.title}"?\n\nL'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`); toast.success("Articolo eliminato"); if (editing?.id === p.id) setEditing(null); load(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const siteBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const articleUrl = `${siteBase}/blog/${form.slug || slugify(form.title)}`;

  /* ---------- VISTA EDITOR ---------- */
  if (editing) {
    return (
      <div className="space-y-6" data-testid="blog-editor">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => { setEditing(null); load(); }} className={`${btn} border-hair hover:border-ink`} data-testid="blog-back"><ArrowLeft size={14} /> Tutti gli articoli</button>
          <div className="flex items-center gap-2 flex-wrap">
            {form.status && <span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS_BADGE[form.status] || ""}`} data-testid="blog-status-badge">{form.status}</span>}
            <button onClick={() => setPreview(true)} className={`${btn} border-hair hover:border-ink`} data-testid="blog-preview"><Eye size={13} /> Anteprima</button>
            <button onClick={saveDraft} disabled={busy} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid="blog-save-draft">Salva bozza</button>
            {form.published && form.status === "pubblicato"
              ? <button onClick={unpublish} disabled={busy} className={`${btn} border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-bone`} data-testid="blog-unpublish">Ritira</button>
              : <button onClick={publish} disabled={busy} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink`} data-testid="blog-publish">{form.published_at && new Date(form.published_at) > new Date() ? "Programma" : "Pubblica"}</button>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:items-start">
          {/* A — Titolo, permalink, contenuto, estratto (mobile: primo) */}
          <div className="order-1 lg:order-none lg:col-span-2 lg:row-start-1 bg-white border border-hair p-4 md:p-6 space-y-4 min-w-0">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.id ? form.slug : slugify(e.target.value) })} className="rounded-none mt-1 font-serif text-xl h-12" placeholder="Titolo dell'articolo" data-testid="blog-title" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Permalink</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-taupe font-mono shrink-0">/blog/</span>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="rounded-none font-mono text-base min-w-0" data-testid="blog-slug" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe block mb-2">Contenuto</Label>
              <ArticleEditor key={form.id || "new"} value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Estratto / riassunto</Label>
              <Textarea rows={3} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-none mt-1 text-base" data-testid="blog-excerpt" />
            </div>
          </div>

          {/* B — Immagini (mobile: dopo il contenuto) */}
          <div className="order-2 lg:order-none lg:col-start-3 lg:row-start-2 lg:self-start bg-white border border-hair p-4 md:p-5 space-y-5 min-w-0">
            <div>
              <div className="eyebrow mb-3">Immagine in evidenza</div>
              <ImageField value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testId="blog-cover" />
            </div>
            <div>
              <div className="eyebrow mb-3">Galleria immagini</div>
              <GalleryManager items={form.gallery || []} onChange={(g) => setForm({ ...form, gallery: g })} withBlogFields testId="blog-gallery" />
            </div>
          </div>

          {/* C — Categoria, tag, autore (mobile: terzo) */}
          <div className="order-3 lg:order-none lg:col-start-3 lg:row-start-3 lg:self-start bg-white border border-hair p-4 md:p-5 space-y-4 min-w-0">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="blog-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Tag (separati da virgola)</Label>
              <Input value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="rounded-none mt-1 text-base" data-testid="blog-tags" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Autore</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-none mt-1 text-base" data-testid="blog-author" />
            </div>
          </div>

          {/* D — SEO (mobile: quarto) */}
          <div className="order-4 lg:order-none lg:col-span-2 lg:row-start-2 bg-white border border-hair p-4 md:p-6 space-y-4 min-w-0" data-testid="blog-seo-panel">
            <div className="eyebrow">SEO</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="min-w-0">
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">SEO title</Label>
                <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder={form.title || "Titolo per i motori di ricerca"} className="rounded-none mt-1 text-base" data-testid="blog-seo-title" />
                <p className="text-[11px] text-taupe mt-1">{(form.seo_title || form.title || "").length}/60 caratteri consigliati</p>
              </div>
              <div className="min-w-0">
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Canonical URL <span className="normal-case tracking-normal">(facoltativo)</span></Label>
                <Input value={form.canonical_url} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} placeholder="https://…" className="rounded-none mt-1 text-base" data-testid="blog-canonical" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Meta description</Label>
              <Textarea rows={2} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder={form.excerpt || "Descrizione per i motori di ricerca"} className="rounded-none mt-1 text-base" data-testid="blog-seo-description" />
              <p className="text-[11px] text-taupe mt-1">{(form.seo_description || form.excerpt || "").length}/155 caratteri consigliati</p>
            </div>
            {/* Anteprima risultato Google */}
            <div className="border border-hair bg-bone p-4 min-w-0 overflow-hidden" data-testid="blog-seo-preview">
              <p className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Anteprima risultato Google</p>
              <p className="text-[#1a0dab] text-lg leading-snug truncate">{form.seo_title || form.title || "Titolo articolo"}</p>
              <p className="text-[#006621] text-xs font-mono truncate">{articleUrl}</p>
              <p className="text-sm text-[#545454] leading-snug line-clamp-2 mt-0.5">{form.seo_description || form.excerpt || "La meta description apparirà qui…"}</p>
            </div>
          </div>

          {/* E — Impostazioni di pubblicazione (mobile: ultimo) */}
          <div className="order-5 lg:order-none lg:col-start-3 lg:row-start-1 lg:self-start bg-white border border-hair p-4 md:p-5 space-y-4 min-w-0">
            <div className="eyebrow">Pubblicazione</div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Data e ora di pubblicazione</Label>
              <input type="datetime-local" value={form.published_at ? form.published_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, published_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="h-11 w-full border border-hair bg-white px-2 text-base rounded-none mt-1" data-testid="blog-published-at" />
              <p className="text-[11px] text-taupe mt-1">Una data futura programma la pubblicazione automatica.</p>
            </div>
          </div>
        </div>

        {/* Anteprima articolo: STESSO renderer e STESSA tipografia della pagina pubblica
            (ArticleContent + .article-content) e stessa larghezza contenitore (max-w-3xl) */}
        <Dialog open={preview} onOpenChange={setPreview}>
          <DialogContent className="rounded-none w-[calc(100vw-1.5rem)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blog-preview-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Anteprima articolo</DialogTitle></DialogHeader>
            <article className="pt-2">
              <span className="eyebrow text-gold">{form.category}</span>
              <h1 className="font-serif text-4xl md:text-6xl leading-[1.08] mt-4 break-words">{form.title || "Senza titolo"}</h1>
              <p className="text-sm text-taupe mt-6">{form.author} · {fmtDate(form.published_at || form.created_at)}</p>
              {form.cover_image && <img src={absoluteMediaUrl(form.cover_image)} alt={form.title} className="w-full h-[320px] md:h-[480px] object-cover mt-10" />}
              <ArticleContent html={toHtml(form.content)} className="mt-10" testId="blog-preview-content" />
            </article>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ---------- VISTA ELENCO ---------- */
  return (
    <div data-testid="blog-panel">
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca articoli…" className="rounded-none pl-9 text-base" data-testid="blog-search" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel} data-testid="blog-filter-status">
          <option value="">Tutti gli stati</option>
          <option value="pubblicato">Pubblicati</option>
          <option value="bozza">Bozze</option>
          <option value="programmato">Programmati</option>
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className={sel} data-testid="blog-filter-category">
          <option value="">Tutte le categorie</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {allTags.length > 0 && (
          <select value={fTag} onChange={(e) => setFTag(e.target.value)} className={sel} data-testid="blog-filter-tag">
            <option value="">Tutti i tag</option>
            {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <button onClick={() => openEditor(null)} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink w-full sm:w-auto min-h-[48px] justify-center`} data-testid="admin-new-blog"><Plus size={14} /> Nuovo articolo</button>
      </div>

      {!posts ? <p className="text-taupe text-sm">Caricamento…</p> : (
        <div className="space-y-3">
          {posts.length === 0 && <div className="p-10 text-center text-taupe bg-white border border-hair">Nessun articolo trovato.</div>}
          {posts.map((p) => (
            <div key={p.id} className="bg-white border border-hair p-4 min-w-0" data-testid={`blog-row-${p.id}`}>
              <div className="flex items-start gap-3">
                {p.cover_image
                  ? <img src={absoluteMediaUrl(p.cover_image)} alt="" className="w-16 h-16 object-cover shrink-0 border border-hair" />
                  : <div className="w-16 h-16 shrink-0 border border-hair bg-bone flex items-center justify-center text-taupe"><ImageIcon size={18} /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-base md:text-lg leading-snug break-words">{p.title}</div>
                  <div className="text-xs text-taupe mt-0.5 font-mono truncate">/blog/{p.slug}</div>
                  <div className="text-xs text-taupe mt-0.5 truncate">{p.category}{p.tags?.length ? ` · ${p.tags.join(", ")}` : ""} · {fmtDate(p.published_at || p.created_at)}</div>
                </div>
                <span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 shrink-0 ${STATUS_BADGE[p.status] || STATUS_BADGE.bozza}`} data-testid={`blog-status-${p.id}`}>{p.status || (p.published ? "pubblicato" : "bozza")}</span>
              </div>
              {/* Azioni: primarie sempre visibili, secondarie nel menu ⋮ */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-hair/60">
                <button onClick={() => openEditor(p)} className={`${btn} border-ink hover:bg-ink hover:text-bone flex-1 sm:flex-none justify-center`} data-testid={`blog-edit-${p.id}`}><Pencil size={13} /> Modifica</button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={`${btn} border-hair hover:border-ink justify-center`} data-testid={`blog-menu-${p.id}`} aria-label="Altre azioni"><MoreVertical size={15} /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none min-w-[200px]">
                    <DropdownMenuItem onClick={() => duplicate(p)} className="min-h-[44px] cursor-pointer" data-testid={`blog-dup-${p.id}`}><Copy size={14} className="mr-2" /> Duplica</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => quickToggle(p)} className="min-h-[44px] cursor-pointer" data-testid={`blog-toggle-${p.id}`}><Eye size={14} className="mr-2" /> {p.published ? "Ritira dalla pubblicazione" : "Pubblica ora"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(p)} className="min-h-[44px] cursor-pointer text-red-700" data-testid={`blog-del-${p.id}`}><Trash2 size={14} className="mr-2" /> Elimina</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
